import { useEffect, useMemo, useState } from "react"
import { debounce } from "lodash-es"
import { MeetingServiceConfigMode } from "./formTypes"

export const handleAutoCompleteMode = (mode?: MeetingServiceConfigMode) => mode || MeetingServiceConfigMode.LOAD_ON_OPEN

export function useAutocompleteController(mode: MeetingServiceConfigMode, wasReset: boolean) {
    const [search, setSearch] = useState("")
    const [debouncedSearch, setDebouncedSearch] = useState("")
    const [open, setOpen] = useState(false)
    const [hasTyped, setHasTyped] = useState(false)
    const [forceRefreshKey, setForceRefreshKey] = useState(0)

    const isSearchAsYouType = mode === MeetingServiceConfigMode.SEARCH_AS_YOU_TYPE
    const isLoadOnOpen = mode === MeetingServiceConfigMode.LOAD_ON_OPEN
    const isEagerLoad = mode === MeetingServiceConfigMode.EAGER_LOAD

    const isSearchValid = debouncedSearch.length === 0 || debouncedSearch.length >= 2
    const enabled =
        (isSearchAsYouType && debouncedSearch.length >= 2) || (isLoadOnOpen && open && isSearchValid) || isEagerLoad

    const debounceSearch = useMemo(() => debounce((v: string) => setDebouncedSearch(v), 300), [])
    useEffect(() => {
        debounceSearch(search)
        return () => debounceSearch.cancel()
    }, [search, debounceSearch])

    useEffect(() => {
        if (wasReset) {
            setForceRefreshKey((k) => k + 1)
            handleClearSearch()
        }
    }, [wasReset])

    const handleInputChange = (_: React.ChangeEvent<{}>, value: string) => {
        if (isSearchAsYouType || open) {
            setSearch(value)
            setHasTyped(true)
        }
    }

    const handleOpen = () => setOpen(true)
    const handleClose = () => {
        setOpen(false)
        if (!hasTyped && isLoadOnOpen) {
            handleClearSearch()
        }
        setHasTyped(false)
    }

    const handleClearSearch = () => {
        setSearch("")
        setDebouncedSearch("")
    }

    return {
        // state
        open,
        debouncedSearch,
        forceRefreshKey,
        enabled,
        isEagerLoad,
        isLoadOnOpen,
        // handlers
        setForceRefreshKey,
        handleInputChange,
        handleOpen,
        handleClose,
        handleClearSearch,
    }
}
